import { EmployeeSync, Portal, User } from '.';
import { LocaleCode } from '../../helpers';
import { Generic } from '../dictionary-service';

export const enum VacanciesListQuickFilterState {
  ANY,
  MY_ACTIVE,
  MY,
  ALL_ACTIVE,
}

export interface Permissions {
  allowArchiveVacancy: boolean;
  dashboardPage: boolean;
  deleteComments: boolean;
  deleteVacancy: boolean;
  editVacancyCloseDate: boolean;
  editVacancyHotStatus: boolean;
  editVacancyInformation: boolean;
  manageEmployees: boolean;
  managePortalFiles: boolean;
  showCandidateStatusCustomFields: boolean;
  showProfileContactDetails: boolean;
  showProfileCustomFields: boolean;
  showProfileSecondName: boolean;
  showProfileSocialLinks: boolean;
  showSalary: boolean;
  showVacancyCustomFields: boolean;
  teammatesPage: boolean;
  vacancyNotificationsTab: boolean;
  vacancyStaticTab: boolean;
  viewPortalProjects: boolean;
  viewProfilesFiles: boolean;
  viewProfilesComments: boolean;
  viewProfilesNotifications: boolean;
  viewProfilesShortlists: boolean;
  viewVacancyComments: boolean;
}

export interface SearchQuery {
  page: number;
  size: number;
  format?: 'short' | 'legacy' | 'full';
  keywords?: string;
}

export interface CreatePayload {
  role: User.Role;
  permissions: Permissions;
  departmentsIds: string[];
  subordinatesIds: number[];
  customProperties: Record<string, string> | null;
  /**
   * User id of the manager (the same id space as `subordinatesIds`); `null`
   * detaches the recruiter from its manager. Omitted — the manager is left as
   * is, so older clients that do not send it keep working.
   */
  managerId?: number | null;
  /** Job title, free text — not the access role. Omitted — left as is. */
  position?: string | null;
  // portalId: number; NB: Portal ID not passed directly to the backend, it is set in the backend from the logged user token
  user: User.CreatePayload;
}

export interface UpdatePayload extends Omit<CreatePayload, 'user'> {
  id: number;
  user: User.UpdatePayload;
}

/**
 * `PATCH recruiters/:id/activity`. Disabling keeps the role, departments,
 * subordinates and permissions, so enabling restores the recruiter as it was.
 */
export interface ActivityPayload {
  isActive: boolean;
}

export interface Base {
  id: number;
  recruiterId: number;
  role: User.Role;
  customProperties: Record<string, string> | null;
  departmentsIds: string[];
  subordinatesIds: number[];
  /** User id of the manager, the same id space as `subordinatesIds`. */
  managerId?: number | null;
  /**
   * Whether the recruiter may sign in. Optional until every service sends it;
   * until then a disabled recruiter is one whose role is DISABLED.
   */
  isActive?: boolean;
  user: User.Base;
  portal: Portal.Base;
}

export interface Details extends Omit<Base, 'user'> {
  user: User.Details;
  portal: Portal.Details;
  permissions: Permissions;
  createdAt: string;
  updatedAt: string;
  departments: Generic.ItemStringified[];
  subordinates: Base[];
  /** ISO date of the last sign-in; `null` if the recruiter never signed in. */
  lastLogin?: string | null;
  /** Job title, free text — not the access role. */
  position?: string | null;
  /**
   * The person's id in the client's employee directory, once the nightly
   * sync linked them; the sync then owns `position` and may switch the
   * recruiter off or on again.
   */
  employeeId?: string | null;
  /** Who switched the recruiter off; `null` while active. */
  deactivationSource?: EmployeeSync.DeactivationSource | null;
}

export interface Legacy extends Base {
  name: string;
}

export interface MyInfo extends Details {
  locale: LocaleCode;
  name: string;
  calendarProvider: CalendarProvider;
}

export type CalendarProvider = 'google' | 'office365' | 'office365Corporate';

/** Persisted (DB) recruiter settings — see recruiter_settings table. */
export interface Settings {
  calendarProvider: CalendarProvider;
}

/** Ephemeral UI preferences kept in Redis. */
export interface QuickSettings {
  lastVacancy: number | null;
  simpleCardViewState: boolean;
  simpleVacancyCardViewState: boolean;
  vacancyQuickFiltersSelection: VacanciesListQuickFilterState;
}
